<template>
    <div
        class="navbar"
        :class="{
            'navbar--overlay': isOverlay,
            'navbar--transparent': showTransparentBar,
            'navbar--active': isMenuHovered,
            'navbar--no-transition': suppressTransition,
        }"
        @mouseover="syncHover"
        @mouseleave="isMenuHovered = false"
    >
        <div class="navbar-container">
            <NavbarLogo :is-visible="!isSubmenuOpen" @close-menu="closeMenu" />
            <NavbarBack :is-visible="isSubmenuOpen" @click="openMenuIndex = null" />
            <NavbarLabel :label="currentMenuLabel" :is-visible="isSubmenuOpen" />
            <div class="navbar__trailing" data-testid="navbar-trailing">
                <NavbarSignupCta class="navbar__trailing-cta" />
                <NavbarBurger :is-open="isMenuOpen" :is-submenu-visible="isSubmenuOpen" @click="isMenuOpen = !isMenuOpen" />
            </div>
            <NavbarContent :is-open="isMenuOpen" :nav="config" :open-menu-index="openMenuIndex" @toggle-menu="toggleMenu" @close-menu="closeMenu" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, shallowRef, type Ref, watch} from 'vue';
    import type {NavbarConfig} from '@navbar-unlogged-types';
    import {useDisplayMobile} from '@malt/utils';
    import NavbarBack from './NavbarBack.vue';
    import NavbarBurger from './NavbarBurger.vue';
    import NavbarContent from './NavbarContent.vue';
    import NavbarLabel from './NavbarLabel.vue';
    import NavbarLogo from './NavbarLogo.vue';
    import NavbarSignupCta from './NavbarSignupCta.vue';
    import {useNavbarConfig} from '../composables/useNavbarConfig';
    import {useNavbarMode} from '../composables/useNavbarMode';
    import {useNavbarTransparency} from '../composables/useNavbarTransparency';

    const {config: moduleConfig, currentModeConfig} = useNavbarMode();
    const {getConfigForDocument, loadAllNavbarConfigs} = useNavbarConfig();

    const documentIds = moduleConfig.value.modes?.map((mode) => mode.prismicDocumentId) ?? [];
    await loadAllNavbarConfigs(documentIds);

    const config = computed(() => getConfigForDocument(currentModeConfig.value?.prismicDocumentId));

    const {isDisplayMobile} = useDisplayMobile('Sm');

    const {isMenuOpen, openMenuIndex, isSubmenuOpen, currentMenuLabel, toggleMenu, closeMenu} = manageNavbarMenu(config, isDisplayMobile);
    const {isOverlay, isTransparent} = useNavbarTransparency(isMenuOpen);
    const isMenuHovered = shallowRef(false);
    const suppressTransition = shallowRef(false);
    const showTransparentBar = computed(() => isTransparent.value && !isMenuHovered.value);

    // `transition: none` has to survive the paint that restores the transparent bar.
    // Clearing it in the same flush as hover-off puts the 0.2s transition back on the
    // after-change style, so the bar fades instead of snapping.
    watch(isMenuHovered, (hovered, _previous, onCleanup) => {
        let frame = 0;
        onCleanup(() => {
            if (frame !== 0) {
                cancelAnimationFrame(frame);
            }
        });

        if (hovered) {
            suppressTransition.value = true;
            return;
        }

        frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => {
                frame = 0;
                suppressTransition.value = false;
            });
        });
    });

    function syncHover(event: MouseEvent) {
        const target = event.target;
        isMenuHovered.value = target instanceof Element && target.closest('.navbar__menu') !== null;
    }

    function manageNavbarMenu(nav: Ref<NavbarConfig>, isDisplayMobile: Ref<boolean>) {
        const isMenuOpen = ref(false);
        const openMenuIndex = ref<number | null>(null);
        const isSubmenuOpen = computed(() => openMenuIndex.value !== null);
        const currentMenuLabel = computed(() => {
            if (openMenuIndex.value !== null && nav.value?.items[openMenuIndex.value]) {
                return nav.value?.items[openMenuIndex.value]!.label ?? '';
            }
            return '';
        });

        watch(isMenuOpen, (open) => {
            document.body.classList.toggle('menu-open', open);
            if (!open) {
                openMenuIndex.value = null;
            }
        });

        const toggleMenu = (index: number) => {
            if (isDisplayMobile.value) {
                openMenuIndex.value = openMenuIndex.value === index ? null : index;
            }
        };

        const closeMenu = () => {
            isMenuOpen.value = false;
            openMenuIndex.value = null;
        };

        return {
            isMenuOpen,
            openMenuIndex,
            isSubmenuOpen,
            currentMenuLabel,
            toggleMenu,
            closeMenu,
        };
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '../styles/navbar-container';

    /* mobile-first */
    .navbar {
        --navbar-text-color: var(--joy-color-neutral-60);
        --navbar-icon-color: var(--joy-color-neutral-50);
        --navbar-logo-filter: none;

        position: sticky;
        top: 0;
        z-index: 1000;
        height: var(--navbar-height);
        width: 100%;
        background-color: var(--joy-color-background-default);
        font-size: var(--joy-font-size-primary-300);
        line-height: 1;
        transition:
            background-color var(--navbar-scroll-transition-duration) var(--navbar-transition-easing),
            border-bottom-color var(--navbar-scroll-transition-duration) var(--navbar-transition-easing);

        > .navbar-container {
            display: flex;
            align-items: flex-end;
            height: 100%;
            gap: var(--joy-core-spacing-3);
        }

        /* Beats the themed transitions on the bar, links, logo and signup button.
           Held one paint after hover ends so leaving snaps back to transparent. */
        &--no-transition {
            &,
            .navbar__menu-item,
            .navbar-unlogged__actions-link,
            .navbar-unlogged__actions-cta .joy-button,
            .navbar__logo-image,
            .unlogged-navbar__locale-selection-button,
            .unlogged-navbar__locale-selection-button * {
                transition: none;
            }
        }

        /* Anchors the mobile drawer (position: fixed) to this bar. */
        transform: translateY(0);
        -webkit-tap-highlight-color: transparent;

        /* Pull the hero up under the bar. The offset stays after scroll so the page does not jump. */
        &--overlay {
            margin-bottom: calc(-1 * var(--navbar-height));
        }

        &--transparent {
            --navbar-text-color: var(--joy-color-neutral-0);
            --navbar-icon-color: var(--joy-color-neutral-0);
            --navbar-logo-filter: brightness(0) invert(1);
            --navbar-cta-surface-color: var(--joy-color-neutral-0);
            --navbar-cta-surface-hover-color: var(--joy-color-neutral-20);
            --navbar-cta-surface-active-color: var(--joy-color-neutral-30);
            --navbar-cta-text-color: var(--joy-color-neutral-70);

            background-color: transparent;
            border-bottom-color: transparent;
        }

        &__trailing {
            margin-left: auto;
            display: flex;
        }

        &__trailing-cta {
            display: flex;
            align-items: center;
            transition: all var(--navbar-transition-duration) var(--navbar-transition-easing);
        }

        &:not(:has(.navbar__content--open)) .navbar__trailing-cta {
            opacity: 1;
            visibility: visible;
        }

        &:has(.navbar__content--open) .navbar__trailing-cta {
            pointer-events: none;
            visibility: hidden;
            opacity: 0;
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .navbar {
            border-bottom: 1px solid var(--joy-color-neutral-30);

            > .navbar-container {
                gap: var(--joy-core-spacing-8);
            }

            &--transparent,
            &--active {
                border-bottom-color: transparent;
            }

            &__trailing {
                display: none;
            }
        }
    }
</style>
